import { postJSON } from "../api.js";
import { esc, opt } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiKpis, uiMoney, uiPill } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * CEO Agenda — the CEO's public life.
 *
 * Invitations arrive every month (keynotes, live TV, podcasts, investor
 * conferences, government roundtables, partner summits, town halls, crisis
 * press conferences, campus talks, the World Economic Forvm). Attending
 * opens the Stage: a live Q&A where each question is typed out, you pick an
 * answer, and the audience reacts — with the real effect on brand, Board
 * confidence, morale or government trust shown at once. Bold answers can
 * become gaffes. Some answers are promises, tracked until their deadline.
 * Data: /api/ceo-agenda, /api/ceo-agenda/{key}/start|answer|decline.
 */

const AG_EFFECT_LABEL = { brand: "Brand", board: "Board confidence", morale: "Morale", government: "Government trust", partner: "Partner trust" };

function agEffects(e) {
  const keys = Object.keys(e || {}).filter((k) => e[k]);
  if (!keys.length) return `<span class="ag-eff zero">No change</span>`;
  return keys.map((k) => `<span class="ag-eff ${e[k] > 0 ? "up" : "down"}">${e[k] > 0 ? "▲" : "▼"} ${esc(AG_EFFECT_LABEL[k] || k)} ${e[k] > 0 ? "+" : ""}${e[k]}</span>`).join("");
}

function agTicket(inv, slotsLeft) {
  const status = inv.status;
  const actions = status === "invited"
    ? `<button type="button" class="primary" data-ag-go="${esc(inv.key)}" ${slotsLeft <= 0 ? "disabled title=\"Your calendar is full this month\"" : ""}>Attend</button>
       <button type="button" data-ag-decline="${esc(inv.key)}">Decline</button>`
    : status === "in_progress" ? `<button type="button" class="primary" data-ag-go="${esc(inv.key)}">Continue (${inv.answered || 0} answered)</button>`
    : status === "done" ? uiPill("Attended", "good") : uiPill("Declined");
  return `
    <article class="ag-ticket ag-${esc(inv.type)} ${status}">
      <div class="ag-ticket-icon">${inv.icon}</div>
      <div class="ag-ticket-body">
        <div class="sx-eyebrow">${esc(inv.label)}</div>
        <h4>${esc(inv.host)}</h4>
        <p>${esc(inv.pitch)}</p>
        <div class="ag-ticket-meta">
          <span>${esc(inv.format)}</span>
          ${inv.audience ? `<span>Audience ${inv.audience.toLocaleString("en-US")}</span>` : ""}
          <span>${inv.questions} questions</span>
          <span>${inv.travel_cost ? `Travel ${uiMoney(inv.travel_cost)}` : "No travel"}</span>
          <span>Impact ×${inv.prestige}</span>
        </div>
      </div>
      <div class="ag-ticket-actions">${actions}</div>
    </article>`;
}

function agPromise(p) {
  const tone = p.status === "kept" ? "good" : p.status === "broken" ? "bad" : "info";
  return `
    <div class="ag-promise ${p.status}">
      <div class="ag-promise-top"><b>${esc(p.label)}</b>${uiPill(p.status === "open" ? `due M${p.deadline_month}` : p.status, tone)}</div>
      <div class="ag-promise-from">Promised at ${esc(p.event_host || "an event")} in month ${p.made_month}</div>
      ${p.status === "open" ? `<div class="ag-promise-bar"><i style="width:${p.progress.pct}%"></i></div><div class="ag-promise-from">${esc(p.progress.detail)}</div>` : ""}
    </div>`;
}

async function renderAgenda(main) {
  const a = await uiGet("/ceo-agenda");
  main.innerHTML = `
    <div class="sx ag">
      ${uiHeader("CEO Agenda", "Invitations to speak, interviews and summits. Every answer you give in public moves your brand, your Board, your people or a government — and every promise is remembered.")}
      ${uiKpis([
        { label: "Brand", value: a.standing.brand },
        { label: "Board confidence", value: a.standing.board },
        { label: "Employee morale", value: a.standing.morale },
        { label: "Calendar this month", value: `${a.slots_left} of ${a.max_per_month} free`, sub: "a CEO's time is limited" },
        { label: "Promises", value: `${a.totals.kept} kept · ${a.totals.broken} broken`, sub: `${a.totals.open} still open` },
      ])}
      ${uiCard(`Invitations · Month ${a.month}`, `<div class="ag-tickets">${a.invitations.map((i) => agTicket(i, a.slots_left)).join("")}</div>`,
        { sub: "New invitations arrive every month and expire at month end" })}
      <div class="sx-grid-2">
        ${uiCard("Promises on the record", a.promises.length ? `<div class="ag-promises">${a.promises.map(agPromise).join("")}</div>`
          : uiEmpty("No public promises yet", "Commitments you make on stage appear here, with their deadline. Keep them and credibility rises; break them and the Board remembers."),
          { sub: "Kept: brand +4, Board +6 · Broken: brand −6, Board −9" })}
        ${uiCard("Past appearances", a.history.length ? `<div class="ag-history">${a.history.map((h) => `
            <div class="ag-hist"><span class="mono">M${h.month}</span><div><b>${esc(h.headline || h.host)}</b><div class="ag-effs">${agEffects(h.effects)}</div></div></div>`).join("")}</div>`
          : uiEmpty("Nothing on the record yet", "Accept an invitation to step on stage."))}
      </div>
    </div>`;
  main.querySelectorAll("[data-ag-go]").forEach((b) => b.addEventListener("click", () => agStage(b.dataset.agGo, () => renderAgenda(main))));
  main.querySelectorAll("[data-ag-decline]").forEach((b) => b.addEventListener("click", async () => {
    const r = await postJSON(`/ceo-agenda/${encodeURIComponent(b.dataset.agDecline)}/decline`, {});
    toast(r.message);
    renderAgenda(main);
  }));
}

function agType(el, text, done) {
  // Typewriter for the question; instant when the user prefers reduced motion.
  const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || !window.requestAnimationFrame) { el.textContent = text; return done(); }
  let i = 0;
  const step = () => {
    i = Math.min(text.length, i + 2);
    el.textContent = text.slice(0, i);
    if (i < text.length) setTimeout(step, 14); else done();
  };
  step();
}

async function agStage(key, onClose) {
  let session;
  try {
    session = await postJSON(`/ceo-agenda/${encodeURIComponent(key)}/start`, {});
  } catch (e) { toast(e.message); return; }
  const inv = session.invitation;
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop ag-stage-wrap";
  backdrop.innerHTML = `
    <div class="ag-stage ag-${esc(inv.type)}">
      <div class="ag-lights"><i></i><i></i><i></i></div>
      <header class="ag-stage-head">
        <div><div class="sx-eyebrow">${inv.icon} ${esc(inv.label)} · ${esc(inv.format)}</div><h3>${esc(inv.host)}</h3></div>
        <div class="ag-dots" id="ag-dots"></div>
      </header>
      <div class="ag-crowd" aria-hidden="true">${Array.from({ length: 36 }, (_, i) => `<span style="--d:${(i % 9) * 0.13}s"></span>`).join("")}</div>
      <div class="ag-floor" id="ag-floor"></div>
      <footer class="ag-stage-foot"><span id="ag-foot-note">${inv.travel_cost ? `Travel ${uiMoney(inv.travel_cost)} charged` : ""}</span>
        <div class="modal-actions"><button id="ag-leave">Leave</button><button class="primary hidden" id="ag-next">Next question</button></div></footer>
    </div>`;
  document.body.appendChild(backdrop);
  const $s = (q) => backdrop.querySelector(q);
  const close = () => { backdrop.remove(); onClose && onClose(); };
  $s("#ag-leave").addEventListener("click", close);
  const dots = (n, total) => { $s("#ag-dots").innerHTML = Array.from({ length: total }, (_, i) => `<i class="${i < n ? "done" : i === n ? "on" : ""}"></i>`).join(""); };

  const showQuestion = (q, answered, total) => {
    dots(answered, total);
    $s("#ag-next").classList.add("hidden");
    $s("#ag-floor").innerHTML = `
      <div class="ag-asker"><div class="ag-mic">🎙</div><div class="ag-bubble"><div class="ag-qnum">Question ${q.index + 1} of ${total}</div><p id="ag-q"></p></div></div>
      <div class="ag-options" id="ag-options">${q.options.map((o) => `
        <button type="button" class="ag-option" data-opt="${o.index}" disabled>
          <span class="ag-style">${esc(o.style)}${o.risky ? ` <em title="Could land brilliantly or backfire">⚡ risky</em>` : ""}${o.promise ? ` <em class="ag-pr" title="A public promise, tracked until its deadline">📌 promise</em>` : ""}</span>
          <span class="ag-label">${esc(o.label)}</span>
        </button>`).join("")}</div>
      <div class="ag-reaction hidden" id="ag-reaction"></div>`;
    agType($s("#ag-q"), q.text, () => {
      backdrop.querySelectorAll(".ag-option").forEach((b, i) => {
        setTimeout(() => { b.disabled = false; b.classList.add("in"); }, i * 90);
        b.addEventListener("click", () => choose(Number(b.dataset.opt), b));
      });
    });
  };

  const choose = async (opt, btn) => {
    backdrop.querySelectorAll(".ag-option").forEach((b) => { b.disabled = true; if (b !== btn) b.classList.add("faded"); });
    btn.classList.add("picked");
    let r;
    try {
      r = await postJSON(`/ceo-agenda/${encodeURIComponent(key)}/answer`, { option: opt });
    } catch (e) { toast(e.message); return; }
    const mood = r.backfired ? "boo" : (Object.values(r.effects).reduce((s, v) => s + v, 0) >= 3 ? "cheer" : "murmur");
    backdrop.querySelector(".ag-stage").dataset.mood = mood;
    const rx = $s("#ag-reaction");
    rx.className = `ag-reaction ${mood}`;
    rx.innerHTML = `
      <div class="ag-rx-title">${r.backfired ? "⚡ It backfired" : mood === "cheer" ? "👏 The room is with you" : "The room reacts"}</div>
      <p>${esc(r.reaction)}</p>
      <div class="ag-effs">${agEffects(r.effects)}</div>
      ${r.promise ? `<div class="ag-promise-made">📌 On the record: “${esc(r.promise)}”</div>` : ""}`;
    dots(r.answered, r.total);
    if (r.done) {
      $s("#ag-next").textContent = "See the headlines";
      $s("#ag-next").classList.remove("hidden");
      $s("#ag-next").onclick = () => finish(r.summary);
    } else {
      $s("#ag-next").textContent = "Next question";
      $s("#ag-next").classList.remove("hidden");
      $s("#ag-next").onclick = () => { delete backdrop.querySelector(".ag-stage").dataset.mood; showQuestion(r.next_question, r.answered, r.total); };
    }
  };

  const finish = (sum) => {
    $s("#ag-next").classList.add("hidden");
    $s("#ag-leave").textContent = "Back to the agenda";
    $s("#ag-floor").innerHTML = `
      <div class="ag-paper">
        <div class="ag-paper-mast">THE DAILY LEDGER · Business</div>
        <h2>${esc(sum.headline)}</h2>
        <div class="ag-effs big">${agEffects(sum.effects)}</div>
        <ol class="ag-recap">${sum.answers.map((x) => `
          <li><div class="ag-recap-q">${esc(x.question_text)}</div>
            <div class="ag-recap-a"><b>${esc(x.style)}:</b> ${esc(x.answer)}${x.backfired ? ` <span class="neg">(backfired)</span>` : ""}</div>
            <div class="ag-recap-r">${esc(x.reaction)}</div></li>`).join("")}</ol>
      </div>`;
    window.refreshAll && window.refreshAll();
  };

  if (session.question) showQuestion(session.question, session.answered, session.total);
  else close();
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.agType = agType;
window.renderAgenda = renderAgenda;
